import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, notificationsApi, paymentsOnlineApi } from '../lib/api';
import useDashboardWebSocket from '../hooks/useDashboardWebSocket';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, GraduationCap, ChalkboardTeacher, Money, Wallet, Warning, Bell, ArrowRight, Lightning, ChatCircle, CheckCircle, XCircle, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GeniuspayAnalytics from '../components/dashboard/GeniuspayAnalytics';
import PushToggleCard from '../components/dashboard/PushToggleCard';

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [stripePending, setStripePending] = useState([]);
  const [geniuspayPending, setGeniuspayPending] = useState([]);
  const [geniuspayEnv, setGeniuspayEnv] = useState(null);
  const [reconciling, setReconciling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);

  const isAdminLike = ['admin', 'comptable'].includes(user?.role);

  useEffect(() => {
    loadStats();
    if (isAdminLike) {
      loadAlerts();
      loadSmsStats();
      loadStripePending();
      loadGeniuspayPending();
      loadGeniuspayEnv();
    }
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await dashboardApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const { data } = await dashboardApi.getAlerts();
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const loadSmsStats = async () => {
    try {
      const { data } = await dashboardApi.getSmsStats(30);
      setSmsStats(data);
    } catch (error) {
      console.error('Failed to load SMS stats:', error);
    }
  };

  const loadStripePending = async () => {
    try {
      const { data } = await paymentsOnlineApi.listPending();
      setStripePending(data || []);
    } catch (error) {
      console.error('Failed to load Stripe pending:', error);
    }
  };

  const reconcileStripe = async (sessionId) => {
    setReconciling(sessionId);
    try {
      const { data } = await paymentsOnlineApi.reconcile(sessionId);
      if (data.reconciled) {
        toast.success(`Paiement Stripe enregistré — ${data.receipt_number} (${Number(data.amount_xof).toLocaleString('fr-FR')} FCFA)`);
      } else if (data.already_processed) {
        toast.info('Déjà réconcilié');
      }
      loadStripePending();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la réconciliation');
    } finally {
      setReconciling(null);
    }
  };

  const loadGeniuspayPending = async () => {
    try {
      const { data } = await paymentsOnlineApi.geniuspayListPending();
      setGeniuspayPending(data || []);
    } catch (error) {
      console.error('Failed to load GeniusPay pending:', error);
    }
  };

  const loadGeniuspayEnv = async () => {
    try {
      const { data } = await paymentsOnlineApi.geniuspayEnv();
      setGeniuspayEnv(data);
    } catch (error) {
      console.error('Failed to load GeniusPay env:', error);
    }
  };

  const reconcileGeniuspay = async (reference) => {
    setReconciling(reference);
    try {
      const { data } = await paymentsOnlineApi.geniuspayReconcile(reference);
      if (data.reconciled) {
        toast.success(`Paiement GeniusPay enregistré — ${data.receipt_number} (${Number(data.amount_xof).toLocaleString('fr-FR')} FCFA)`);
      } else if (data.already_processed) {
        toast.info('Déjà réconcilié');
      }
      loadGeniuspayPending();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la réconciliation GeniusPay');
    } finally {
      setReconciling(null);
    }
  };

  // Real-time event handler (WebSocket)
  const handleEvent = useCallback((event, data) => {
    if (event === 'payment.created') {
      const amount = new Intl.NumberFormat('fr-FR').format(data.amount || 0);
      toast.success(
        `${language === 'fr' ? 'Nouveau paiement' : 'New payment'}: ${data.student_name} — ${amount} FCFA`,
        { duration: 5000 }
      );
      setLiveEvents((prev) => [{ ...data, event, ts: Date.now() }, ...prev].slice(0, 10));
      // Refresh stats (lightweight) so today_payments updates immediately
      loadStats();
    } else if (event === 'notification.alert') {
      if (isAdminLike) {
        if (data.status === 'failed') {
          toast.error(
            `${language === 'fr' ? 'Notification échouée' : 'Notification failed'}: ${data.channel.toUpperCase()} → ${data.to}`,
            { duration: 6000 }
          );
        }
        setLiveEvents((prev) => [{ ...data, event, ts: Date.now() }, ...prev].slice(0, 10));
        // Refresh aggregated alerts + SMS stats
        loadAlerts();
        if (data.channel === 'sms') loadSmsStats();
      }
    } else if (event === 'security.alert') {
      if (isAdminLike) {
        toast.error(
          `${language === 'fr' ? 'Alerte sécurité' : 'Security alert'}: ${data.failure_count} ${language === 'fr' ? 'échecs depuis' : 'failures from'} ${data.ip}`,
          { duration: 10000 }
        );
        setLiveEvents((prev) => [{ ...data, event, ts: Date.now() }, ...prev].slice(0, 10));
      }
    }
  }, [language, isAdminLike]);

  const { connected: wsConnected } = useDashboardWebSocket(handleEvent);

  const sendBalanceReminders = async () => {
    setReminderLoading(true);
    try {
      const { data } = await notificationsApi.remindBalances({ threshold: 0 });
      toast.success(`${data.sent_email} ${language === 'fr' ? 'emails envoyés' : 'emails sent'}`);
      loadAlerts();
    } catch {
      toast.error(t('error'));
    } finally {
      setReminderLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const statCards = [
    {
      title: t('totalStudents'),
      value: stats?.students || 0,
      icon: GraduationCap,
      color: '#0052FF',
      bgColor: '#EFF6FF'
    },
    {
      title: t('totalTeachers'),
      value: stats?.teachers || 0,
      icon: ChalkboardTeacher,
      color: '#10B981',
      bgColor: '#ECFDF5'
    },
    {
      title: t('totalStaff'),
      value: stats?.staff || 0,
      icon: Users,
      color: '#F59E0B',
      bgColor: '#FFFBEB'
    },
    {
      title: t('todayPayments'),
      value: formatCurrency(stats?.today_payments),
      icon: Money,
      color: '#0EA5E9',
      bgColor: '#F0F9FF'
    },
    {
      title: t('monthPayments'),
      value: formatCurrency(stats?.month_payments),
      icon: Wallet,
      color: '#8B5CF6',
      bgColor: '#F5F3FF'
    },
    {
      title: t('outstandingBalance'),
      value: formatCurrency(stats?.outstanding_balance),
      icon: Warning,
      color: '#EF4444',
      bgColor: '#FEF2F2'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Banner */}
      <div 
        className="relative rounded-lg overflow-hidden bg-cover bg-center h-48"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/6c3e65fdc098a236208decff79887da29de5eaab226836321278bd67cc31fe5d.png)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0052FF]/90 to-[#0052FF]/60" />
        <div className="relative z-10 h-full flex flex-col justify-center px-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-outfit text-3xl font-bold text-white">
              {t('welcome')}, {user?.name}!
            </h1>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                wsConnected ? 'bg-green-400/20 text-green-100' : 'bg-amber-400/20 text-amber-100'
              }`}
              data-testid="ws-status-badge"
              title={wsConnected ? (language === 'fr' ? 'Mises à jour en temps réel' : 'Live updates') : (language === 'fr' ? 'Reconnexion…' : 'Reconnecting…')}
            >
              <Lightning size={12} weight={wsConnected ? 'fill' : 'regular'} />
              {wsConnected ? (language === 'fr' ? 'Direct' : 'Live') : (language === 'fr' ? 'Hors ligne' : 'Offline')}
            </span>
          </div>
          <p className="font-manrope text-white/90">
            {language === 'fr' 
              ? `Rôle: ${t(user?.role) || user?.role}`
              : `Role: ${t(user?.role) || user?.role}`}
          </p>
        </div>
      </div>

      {/* Alerts Banner (admin/comptable only) */}
      {alerts && (alerts.notifications.failed > 0 || alerts.late_balances.count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-alerts">
          {alerts.notifications.failed > 0 && (
            <Card className="border-l-4 border-l-red-500 border-y border-r border-[#E2E8F0] shadow-none" data-testid="alert-notifications">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-red-50">
                      <Bell size={20} className="text-red-600" weight="duotone" />
                    </div>
                    <div>
                      <p className="font-outfit font-semibold text-[#0F172A]">
                        {alerts.notifications.failed} {language === 'fr' ? 'notifications échouées' : 'failed notifications'}
                      </p>
                      <p className="font-manrope text-sm text-[#64748B]">
                        {language === 'fr' ? `Dernières ${alerts.since_hours}h` : `Last ${alerts.since_hours}h`} · {alerts.notifications.sent} {language === 'fr' ? 'envoyées' : 'sent'}
                      </p>
                    </div>
                  </div>
                  <Link to="/notifications" data-testid="alert-view-logs">
                    <Button variant="ghost" size="sm" className="text-[#0052FF]">
                      {language === 'fr' ? 'Voir' : 'View'} <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {alerts.late_balances.count > 0 && (
            <Card className="border-l-4 border-l-amber-500 border-y border-r border-[#E2E8F0] shadow-none" data-testid="alert-late-balances">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <Warning size={20} className="text-amber-600" weight="duotone" />
                    </div>
                    <div>
                      <p className="font-outfit font-semibold text-[#0F172A]">
                        {alerts.late_balances.count} {language === 'fr' ? `élèves en retard de paiement (>${alerts.late_balances.late_days}j)` : `students overdue (>${alerts.late_balances.late_days}d)`}
                      </p>
                      <p className="font-manrope text-sm text-[#64748B]">
                        {formatCurrency(alerts.late_balances.total_amount)} {language === 'fr' ? 'au total' : 'total'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendBalanceReminders}
                    disabled={reminderLoading}
                    data-testid="alert-send-reminders"
                  >
                    <Bell size={14} className="mr-1" />
                    {language === 'fr' ? 'Envoyer rappels' : 'Send reminders'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* SMS Delivery Widget (30 days) */}
      {isAdminLike && smsStats && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="sms-stats-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit text-base flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-indigo-50">
                  <ChatCircle size={18} className="text-indigo-600" weight="duotone" />
                </div>
                {language === 'fr' ? 'Livraison SMS (30j)' : 'SMS delivery (30d)'}
              </CardTitle>
              <Link to="/notifications" data-testid="sms-stats-view-logs">
                <Button variant="ghost" size="sm" className="text-[#0052FF] text-xs">
                  {language === 'fr' ? 'Logs' : 'Logs'} <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="p-3 rounded-lg bg-emerald-50/60 border border-emerald-100" data-testid="sms-sent">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={16} className="text-emerald-600" weight="duotone" />
                  <p className="text-xs font-manrope text-[#64748B]">{language === 'fr' ? 'Envoyés' : 'Sent'}</p>
                </div>
                <p className="font-outfit text-xl font-semibold text-emerald-700">{smsStats.sent}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50/60 border border-red-100" data-testid="sms-failed">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle size={16} className="text-red-600" weight="duotone" />
                  <p className="text-xs font-manrope text-[#64748B]">{language === 'fr' ? 'Échecs' : 'Failed'}</p>
                </div>
                <p className="font-outfit text-xl font-semibold text-red-700">{smsStats.failed}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50/60 border border-amber-100" data-testid="sms-disabled">
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={16} className="text-amber-600" weight="duotone" />
                  <p className="text-xs font-manrope text-[#64748B]">{language === 'fr' ? 'Non envoyés' : 'Skipped'}</p>
                </div>
                <p className="font-outfit text-xl font-semibold text-amber-700">{smsStats.disabled}</p>
              </div>
              <div className="p-3 rounded-lg bg-indigo-50/60 border border-indigo-100" data-testid="sms-rate">
                <div className="flex items-center gap-2 mb-1">
                  <Lightning size={16} className="text-indigo-600" weight="duotone" />
                  <p className="text-xs font-manrope text-[#64748B]">{language === 'fr' ? 'Taux livraison' : 'Delivery rate'}</p>
                </div>
                <p className="font-outfit text-xl font-semibold text-indigo-700">
                  {smsStats.delivery_rate === null ? '—' : `${smsStats.delivery_rate}%`}
                </p>
              </div>
            </div>
            {smsStats.recent_failures?.length > 0 && (
              <div className="border-t border-[#E2E8F0] pt-3" data-testid="sms-recent-failures">
                <p className="font-outfit text-xs font-semibold text-[#0F172A] mb-2">
                  {language === 'fr' ? 'Derniers échecs' : 'Recent failures'}
                </p>
                <ul className="space-y-1.5">
                  {smsStats.recent_failures.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs font-manrope">
                      <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                      <span className="text-[#0F172A] font-mono">{f.to}</span>
                      <span className="text-[#64748B] truncate">— {f.error}</span>
                      <span className="text-[#94A3B8] ml-auto shrink-0">{(f.created_at || '').slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stripe pending reconciliations (admin/comptable only) */}
      {isAdminLike && stripePending.length > 0 && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="stripe-pending-widget">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-purple-50">
                <CreditCard size={18} className="text-purple-600" weight="duotone" />
              </div>
              Paiements Stripe en attente
              <span className="ml-2 inline-flex items-center justify-center min-w-[24px] px-2 h-6 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                {stripePending.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs font-manrope text-[#64748B] mb-3">
              Sessions Stripe créées mais dont le paiement n&apos;a pas encore été confirmé automatiquement (webhook en attente ou perdu). Réconciliez manuellement après avoir vérifié dans votre tableau de bord Stripe que la carte a bien été débitée.
            </p>
            <div className="space-y-2 max-h-64 overflow-auto">
              {stripePending.slice(0, 10).map((tx) => (
                <div
                  key={tx.session_id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E2E8F0] hover:bg-purple-50/30"
                  data-testid={`stripe-pending-${tx.session_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-outfit text-sm font-semibold text-[#0F172A] truncate">
                      {tx.student_name} <span className="text-xs text-[#64748B]">({tx.label})</span>
                    </p>
                    <p className="text-xs font-manrope text-[#64748B] font-mono truncate">
                      {tx.session_id}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-outfit text-sm font-semibold text-purple-700">
                      {Number(tx.amount_xof || 0).toLocaleString('fr-FR')} FCFA
                    </p>
                    <p className="text-xs text-[#94A3B8]">{(tx.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={reconciling === tx.session_id}
                    onClick={() => reconcileStripe(tx.session_id)}
                    className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                    data-testid={`reconcile-btn-${tx.session_id}`}
                  >
                    {reconciling === tx.session_id ? '…' : 'Confirmer'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* GeniusPay environment badge (always visible for admin/comptable) */}
      {isAdminLike && geniuspayEnv && (
        <Card
          className={`border shadow-none ${
            geniuspayEnv.effective_mode === 'live'
              ? 'border-emerald-200 bg-emerald-50/40'
              : geniuspayEnv.effective_mode === 'sandbox'
              ? 'border-amber-200 bg-amber-50/40'
              : 'border-red-200 bg-red-50/40'
          }`}
          data-testid="geniuspay-env-badge"
        >
          <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className={`p-1.5 rounded-md ${
                  geniuspayEnv.effective_mode === 'live' ? 'bg-emerald-100' : 'bg-amber-100'
                }`}
              >
                <CreditCard
                  size={18}
                  className={
                    geniuspayEnv.effective_mode === 'live' ? 'text-emerald-700' : 'text-amber-700'
                  }
                  weight="duotone"
                />
              </div>
              <div>
                <p className="font-outfit text-sm font-semibold text-[#0F172A]">
                  GeniusPay —{' '}
                  <span
                    className={
                      geniuspayEnv.effective_mode === 'live'
                        ? 'text-emerald-700'
                        : geniuspayEnv.effective_mode === 'sandbox'
                        ? 'text-amber-700'
                        : 'text-red-700'
                    }
                    data-testid="geniuspay-env-mode"
                  >
                    {geniuspayEnv.effective_mode === 'live'
                      ? 'PRODUCTION (Live)'
                      : geniuspayEnv.effective_mode === 'sandbox'
                      ? 'SANDBOX (Test)'
                      : 'CONFIGURATION INCOHÉRENTE'}
                  </span>
                </p>
                <p className="text-xs font-manrope text-[#64748B]">
                  Clé : <span className="font-mono">{geniuspayEnv.api_key?.masked || '—'}</span>
                  {' · '}
                  Webhook :{' '}
                  <span className={geniuspayEnv.webhook_secret?.configured ? 'text-emerald-700' : 'text-red-600 font-semibold'}>
                    {geniuspayEnv.webhook_secret?.configured ? 'configuré' : 'NON configuré'}
                  </span>
                </p>
              </div>
            </div>
            {geniuspayEnv.effective_mode === 'live' && !geniuspayEnv.webhook_secret?.configured && (
              <div className="text-xs text-red-700 max-w-md" data-testid="geniuspay-webhook-warning">
                ⚠ Enregistrez votre webhook production dans le dashboard GeniusPay et
                collez le secret <code className="bg-white px-1 rounded">whsec_live_...</code> dans{' '}
                <code className="bg-white px-1 rounded">GENIUSPAY_WEBHOOK_SECRET</code>.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Push notifications toggle (all authenticated staff — parents get theirs on ParentDashboard) */}
      {user && <PushToggleCard />}

      {/* GeniusPay analytics — visible whenever env badge is loaded (admin/comptable) */}
      {isAdminLike && geniuspayEnv && <GeniuspayAnalytics />}

      {/* GeniusPay pending reconciliations (admin/comptable only) */}
      {isAdminLike && geniuspayPending.length > 0 && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="geniuspay-pending-widget">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-emerald-50">
                <CreditCard size={18} className="text-emerald-600" weight="duotone" />
              </div>
              Paiements GeniusPay en attente
              <span className="ml-2 inline-flex items-center justify-center min-w-[24px] px-2 h-6 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                {geniuspayPending.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs font-manrope text-[#64748B] mb-3">
              Transactions GeniusPay (Wave / Orange / MTN / Cartes) créées mais pas encore confirmées (webhook perdu ou paiement en cours). Cliquez sur « Confirmer » pour interroger GeniusPay et enregistrer le paiement si la transaction est complétée.
            </p>
            <div className="space-y-2 max-h-64 overflow-auto">
              {geniuspayPending.slice(0, 10).map((tx) => (
                <div
                  key={tx.reference}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E2E8F0] hover:bg-emerald-50/30"
                  data-testid={`geniuspay-pending-${tx.reference}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-outfit text-sm font-semibold text-[#0F172A] truncate">
                      {tx.student_name} <span className="text-xs text-[#64748B]">({tx.label})</span>
                    </p>
                    <p className="text-xs font-manrope text-[#64748B] font-mono truncate">
                      {tx.reference}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-outfit text-sm font-semibold text-emerald-700">
                      {Number(tx.amount_xof || 0).toLocaleString('fr-FR')} FCFA
                    </p>
                    <p className="text-xs text-[#94A3B8]">{(tx.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={reconciling === tx.reference}
                    onClick={() => reconcileGeniuspay(tx.reference)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    data-testid={`reconcile-geniuspay-${tx.reference}`}
                  >
                    {reconciling === tx.reference ? '…' : 'Confirmer'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="border border-[#E2E8F0] shadow-none hover:-translate-y-1 transition-all duration-200"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-manrope text-sm text-[#64748B] mb-1">{stat.title}</p>
                  <p className="font-outfit text-2xl font-bold text-[#0F172A]">{stat.value}</p>
                </div>
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: stat.bgColor }}
                >
                  <stat.icon size={24} style={{ color: stat.color }} weight="duotone" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions based on role */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-lg text-[#0F172A]">
            {language === 'fr' ? 'Actions rapides' : 'Quick Actions'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(user?.role === 'admin' || user?.role === 'user') && (
              <QuickActionCard 
                title={t('addStudent')}
                icon={GraduationCap}
                href="/students?action=add"
              />
            )}
            {(user?.role === 'admin' || user?.role === 'cashier') && (
              <QuickActionCard 
                title={t('addPayment')}
                icon={Money}
                href="/payments?action=add"
              />
            )}
            {(user?.role === 'admin' || user?.role === 'director_studies') && (
              <QuickActionCard 
                title={t('addBulletin')}
                icon={ChalkboardTeacher}
                href="/grades"
              />
            )}
            {(user?.role === 'admin' || user?.role === 'comptable') && (
              <QuickActionCard 
                title={t('financialReport')}
                icon={Wallet}
                href="/reports"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Live Activity Feed (real-time) */}
      {liveEvents.length > 0 && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="live-activity-feed">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Lightning size={18} className="text-[#0052FF]" weight="fill" />
              {language === 'fr' ? 'Activité en temps réel' : 'Live activity'}
              <span className="text-xs font-normal text-[#64748B] ml-auto">
                {language === 'fr' ? `${liveEvents.length} récent(s)` : `${liveEvents.length} recent`}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {liveEvents.map((evt, idx) => (
                <li
                  key={`${evt.ts}-${idx}`}
                  className="flex items-start gap-3 text-sm py-1.5 border-b border-[#F1F5F9] last:border-0"
                  data-testid={`live-event-${idx}`}
                >
                  {evt.event === 'payment.created' ? (
                    <Money size={16} className="text-[#10B981] mt-0.5" weight="duotone" />
                  ) : (
                    <Bell size={16} className="text-red-500 mt-0.5" weight="duotone" />
                  )}
                  <div className="flex-1 min-w-0">
                    {evt.event === 'payment.created' ? (
                      <>
                        <p className="text-[#0F172A] truncate">
                          <span className="font-semibold">{evt.student_name}</span>
                          {' · '}
                          {evt.payment_type}
                          {' · '}
                          <span className="text-[#10B981] font-medium">
                            {new Intl.NumberFormat('fr-FR').format(evt.amount || 0)} FCFA
                          </span>
                        </p>
                        <p className="text-xs text-[#64748B]">{evt.receipt_number}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[#0F172A] truncate">
                          <span className="font-semibold uppercase">{evt.channel}</span>
                          {' → '}
                          {evt.to}
                          {' · '}
                          <span className={evt.status === 'failed' ? 'text-red-600' : 'text-amber-600'}>
                            {evt.status}
                          </span>
                        </p>
                        {evt.error && <p className="text-xs text-[#64748B] truncate">{evt.error}</p>}
                      </>
                    )}
                  </div>
                  <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                    {new Date(evt.ts).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const QuickActionCard = ({ title, icon: Icon, href }) => (
  <a 
    href={href}
    className="flex flex-col items-center p-4 rounded-lg border border-[#E2E8F0] hover:bg-[#F1F5F9] hover:-translate-y-1 transition-all duration-200"
    data-testid={`quick-action-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon size={32} className="text-[#0052FF] mb-2" weight="duotone" />
    <span className="font-manrope text-sm text-center text-[#0F172A]">{title}</span>
  </a>
);

export default Dashboard;
